import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { TiHomeOutline } from "react-icons/ti";
import {
  RiFileListLine,
  RiCalendarEventLine,
  RiMapPinLine,
  RiNewspaperLine,
  RiFootballLine,
  RiMenu4Line,
  RiDashboardLine,
  RiArrowRightSLine,
  RiChatQuoteLine,
  RiInformationLine,
  RiArrowDownSLine,
  RiAppsLine,
  RiPriceTag3Line,
  RiCake2Line,
} from "react-icons/ri";
import { GrContactInfo } from "react-icons/gr";
import { MdOutlineEventNote, MdSavedSearch } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import { TbMessageChatbot } from "react-icons/tb";
import { PiFlagBannerBold } from "react-icons/pi";
import { Goal, Sparkles, Ticket } from "lucide-react";
import { RiLightbulbFlashLine } from "react-icons/ri";
import { IoRestaurantOutline } from "react-icons/io5";
import { FaUserTie } from "react-icons/fa";

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => {
      if (prev[sectionKey]) {
        return {
          ...prev,
          [sectionKey]: false,
        };
      }
      return {
        [sectionKey]: true,
      };
    });
  };

  const menuItems = [
    {
      label: "Dashboard",
      icon: <RiDashboardLine size={20} />,
      path: "/dashboard",
      type: "single",
    },
    {
      label: "Bookings",
      icon: <RiCalendarEventLine size={20} />,
      path: "/events-bookings",
      type: "single",
    },
    {
      label: "Info",
      icon: <RiInformationLine size={20} />,
      type: "section",
      key: "info",
      children: [
        {
          path: "/events",
          icon: <MdOutlineEventNote size={18} />,
          label: "Events",
        },
        {
          path: "/all-fields",
          icon: <RiMapPinLine size={18} />,
          label: "All Fields",
        },
        {
          path: "/sports-categories",
          icon: <RiFootballLine size={18} />,
          label: "All Sports",
        },
        {
          path: "/news-blogs",
          icon: <RiNewspaperLine size={18} />,
          label: "News Blogs",
        },
        {
          path: "/testimonials",
          icon: <RiChatQuoteLine size={18} />,
          label: "Testimonials",
        },
        {
          path: "/admin-faqs",
          icon: <RiLightbulbFlashLine size={18} />,
          label: "FAQs",
        },
        {
          path: "/job-posts",
          icon: <RiLightbulbFlashLine size={18} />,
          label: "Job Posts",
        },
      ],
    },
    {
      label: "Inquiries",
      icon: <MdSavedSearch size={20} />,
      type: "section",
      key: "inquiries",
      children: [
        {
          path: "/inquiries",
          icon: <MdSavedSearch size={18} />,
          label: "All Inquiries",
        },
        {
          path: "/admin-party-requests",
          icon: <RiCake2Line size={18} />,
          label: "Parties & Events",
        },
        {
          path: "/chat-bot-inquiries",
          icon: <TbMessageChatbot size={18} />,
          label: "Chatbot",
        },
        {
          path: "/job-applications",
          icon: <FaUserTie size={18} />,
          label: "Job Req",
        },
      ],
    },
    {
      label: "Contents",
      icon: <RiFileListLine size={20} />,
      type: "section",
      key: "contents",
      children: [
        {
          path: "/combined-home-contents",
          icon: <TiHomeOutline size={18} />,
          label: "Home",
        },
        {
          path: "/contact-info",
          icon: <GrContactInfo size={18} />,
          label: "Contact Info",
        },
        {
          path: "/rental-content",
          icon: <Goal size={18} />,
          label: "Rentals",
        },
        {
          path: "/membership-content",
          icon: <Ticket size={18} />,
          label: "Memberships",
        },
        {
          path: "/sports-academy-content",
          icon: <Sparkles size={18} />,
          label: "Benefits",
        },
        {
          path: "/all-page-banner-data",
          icon: <PiFlagBannerBold size={18} />,
          label: "Banner title",
        },
        {
          path: "/extra-contents",
          icon: <RiAppsLine size={18} />,
          label: "Extras",
        },
        {
          path: "/bar-restaurants-contents",
          icon: <IoRestaurantOutline size={18} />,
          label: "Bar & Rest",
        },
        {
          path: "/admin-event-packages",
          icon: <RiPriceTag3Line size={18} />,
          label: "Event Packages",
        },
      ],
    },
  ];

  const isChildActive = (children) => {
    return children.some((child) => location.pathname === child.path);
  };

  const renderMenuItem = (item) => {
    if (item.type === "single") {
      return (
        <motion.li
          key={item.path}
          whileHover={{ x: 4 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          <div
            onClick={() => navigate(item.path)}
            className={`${
              location.pathname === item.path
                ? "bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white font-medium shadow-lg"
                : "text-primaryTextColor font-normal hover:bg-gray-100"
            } flex items-center cursor-pointer p-3 text-sm rounded-xl transition-all duration-300 group`}
          >
            <span
              className={`${
                location.pathname === item.path
                  ? "text-white"
                  : "text-primaryBtnColor"
              } transition-colors duration-300`}
            >
              {item.icon}
            </span>
            <span className="ml-4 tracking-wide font-poppins">
              {item.label}
            </span>
            <motion.span
              className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              initial={{ opacity: 0, x: -10 }}
              animate={{
                opacity: location.pathname === item.path ? 1 : 0,
                x: location.pathname === item.path ? 0 : -10,
              }}
            >
              <RiArrowRightSLine size={18} />
            </motion.span>
          </div>
        </motion.li>
      );
    }

    if (item.type === "section") {
      const isExpanded = expandedSections[item.key];
      const hasActiveChild = isChildActive(item.children);

      return (
        <li key={item.key}>
          <div
            onClick={() => toggleSection(item.key)}
            className={`${
              hasActiveChild
                ? "bg-gradient-to-r from-primaryBtnColor/10 to-primaryBtnHoverColor/10 text-primaryBtnColor font-medium"
                : "text-primaryTextColor font-normal hover:bg-gray-100"
            } flex items-center cursor-pointer p-3 text-sm rounded-xl transition-all duration-300 group`}
          >
            <span
              className={`${
                hasActiveChild ? "text-primaryBtnColor" : "text-primaryBtnColor"
              } transition-colors duration-300`}
            >
              {item.icon}
            </span>
            <span className="ml-4 tracking-wide font-poppins">
              {item.label}
            </span>
            <motion.span
              className="ml-auto"
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <RiArrowDownSLine size={20} />
            </motion.span>
          </div>

          <AnimatePresence>
            {isExpanded && (
              <motion.ul
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-2 ml-1 space-y-0.5 overflow-hidden"
              >
                {item.children.map((child) => (
                  <motion.li
                    key={child.path}
                    whileHover={{ x: 4 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <div
                      onClick={() => navigate(child.path)}
                      className={`${
                        location.pathname === child.path
                          ? "bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white font-medium shadow-md"
                          : "text-gray-800 font-normal hover:bg-gray-50"
                      } flex items-center cursor-pointer p-2.5 pl-3 text-sm rounded-lg transition-all duration-300 group`}
                    >
                      <span
                        className={`${
                          location.pathname === child.path
                            ? "text-white"
                            : "text-gray-500"
                        } transition-colors duration-300`}
                      >
                        {child.icon}
                      </span>
                      <span className="ml-3 tracking-wide font-poppins text-sm">
                        {child.label}
                      </span>
                      <motion.span
                        className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{
                          opacity: location.pathname === child.path ? 1 : 0,
                          x: location.pathname === child.path ? 0 : -10,
                        }}
                      >
                        <RiArrowRightSLine size={16} />
                      </motion.span>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </li>
      );
    }
  };

  return (
    <React.Fragment>
      <div
        className="lg:contents hidden flex-shrink-0 tracking-wide hide-scrollbar"
        aria-label="Sidebar"
      >
        <div className="overflow-y-auto pb-2 px-4 pt-5 bg-white h-screen relative shadow-2xl hide-scrollbar">
          <div className="px-2 flex flex-col items-center">
            <div className="w-14 h-14 bg-gradient-to-br from-primaryBtnColor to-primaryBtnHoverColor rounded-2xl flex items-center justify-center shadow-lg mb-2">
              <div className="text-white text-2xl font-bold">SS</div>
            </div>
            <h1 className="text-xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
              Strikes & Shots
            </h1>
            <p className="text-xs font-subheading text-gray-400 mt-1">
              Premium Sports Hub
            </p>

            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-3"></div>
          </div>

          <div className="px-1">
            <ul className="space-y-1 tracking-wide">
              {menuItems.map((item) => renderMenuItem(item))}
            </ul>
          </div>
        </div>
      </div>

      <div className="block lg:hidden">
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              className="h-full w-[70%] sm:w-[60%] absolute z-40 shadow-2xl"
              initial={{ opacity: 0, x: "-100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "-100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className="overflow-y-auto py-6 px-4 bg-white h-screen relative hide-scrollbar">
                <div className="mb-3 px-2 flex items-center">
                  <div
                    onClick={toggleSidebar}
                    className="w-12 h-12 bg-gradient-to-br from-primaryBtnColor to-primaryBtnHoverColor rounded-xl flex items-center justify-center shadow-lg mr-3 cursor-pointer"
                  >
                    {isSidebarOpen ? (
                      <IoClose size={24} className="text-white" />
                    ) : (
                      <RiMenu4Line size={24} className="text-white" />
                    )}
                  </div>

                  <div>
                    <h1 className="text-xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
                      Strikes & Shots
                    </h1>
                    <p className="text-xs font-subheading text-gray-400">
                      Premium Sports Hub
                    </p>
                  </div>
                </div>

                <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-4"></div>

                <div className="px-1">
                  <ul className="space-y-1 tracking-wide">
                    {menuItems.map((item) => renderMenuItem(item))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </React.Fragment>
  );
};

export default Sidebar;
